import {
    animate,
    AnimationBuilder,
    AnimationPlayer,
    style,
} from '@angular/animations';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ScrollStrategy, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { CommonModule, DOCUMENT, NgFor, NgIf } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import {
    FuseNavigationItem,
    FuseVerticalNavigationAppearance,
    FuseVerticalNavigationMode,
    FuseVerticalNavigationPosition,
} from '@fuse/components/navigation/navigation.types';
import { FuseVerticalNavigationAsideItemComponent } from '@fuse/components/navigation/vertical/components/aside/aside.component';
import { FuseVerticalNavigationBasicItemComponent } from '@fuse/components/navigation/vertical/components/basic/basic.component';
import { FuseVerticalNavigationCollapsableItemComponent } from '@fuse/components/navigation/vertical/components/collapsable/collapsable.component';
import { FuseVerticalNavigationDividerItemComponent } from '@fuse/components/navigation/vertical/components/divider/divider.component';
import { FuseVerticalNavigationGroupItemComponent } from '@fuse/components/navigation/vertical/components/group/group.component';
import { FuseVerticalNavigationSpacerItemComponent } from '@fuse/components/navigation/vertical/components/spacer/spacer.component';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar/scrollbar.directive';
import { FuseUtilsService } from '@fuse/services/utils/utils.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { UserService } from 'app/core/user/user.service';
import { Company } from 'app/core/user/user.types';
import {
    delay,
    filter,
    merge,
    ReplaySubject,
    Subject,
    Subscription,
    takeUntil,
} from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';
type NavItem = FuseNavigationItem & {
    role?: string;
    secondRole?: string;
    thirdRole?: string;
    permissionKey?: string;
    hidden?: boolean | ((i: NavItem) => boolean);
    children?: NavItem[];
};

@Component({
    selector: 'fuse-vertical-navigation',
    templateUrl: './vertical.component.html',
    styleUrls: ['./vertical.component.scss'],
    animations: fuseAnimations,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'fuseVerticalNavigation',
    standalone: true,
    imports: [
        FuseScrollbarDirective,
        NgFor,
        NgIf,
        FuseVerticalNavigationAsideItemComponent,
        FuseVerticalNavigationBasicItemComponent,
        FuseVerticalNavigationCollapsableItemComponent,
        FuseVerticalNavigationDividerItemComponent,
        FuseVerticalNavigationGroupItemComponent,
        FuseVerticalNavigationSpacerItemComponent,
        CommonModule
    ],
})
export class FuseVerticalNavigationComponent
    implements OnChanges, OnInit, AfterViewInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_inner: BooleanInput;
    static ngAcceptInputType_opened: BooleanInput;
    static ngAcceptInputType_transparentOverlay: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() appearance: FuseVerticalNavigationAppearance = 'default';
    @Input() autoCollapse: boolean = true;
    @Input() inner: boolean = false;
    @Input() mode: FuseVerticalNavigationMode = 'side';
    @Input() name: string = inject(FuseUtilsService).randomId();
    @Input() navigation: NavItem[] = [];
    @Input() opened: boolean = true;
    @Input() position: FuseVerticalNavigationPosition = 'left';
    @Input() transparentOverlay: boolean = false;

    @Output() readonly appearanceChanged =
        new EventEmitter<FuseVerticalNavigationAppearance>();
    @Output() readonly modeChanged =
        new EventEmitter<FuseVerticalNavigationMode>();
    @Output() readonly openedChanged = new EventEmitter<boolean>();
    @Output() readonly positionChanged =
        new EventEmitter<FuseVerticalNavigationPosition>();

    @ViewChild('navigationContent') private _navigationContentEl!: ElementRef;

    // Rendered (filtered) tree
    navToRender: NavItem[] = [];

    // State
    activeAsideItemId: string | null = null;
    onCollapsableItemCollapsed = new ReplaySubject<FuseNavigationItem>(1);
    onCollapsableItemExpanded = new ReplaySubject<FuseNavigationItem>(1);
    onRefreshed = new ReplaySubject<boolean>(1);

    private _animationsEnabled = false;
    private _asideOverlay!: HTMLElement | null;
    private readonly _handleAsideOverlayClick: any;
    private readonly _handleOverlayClick: any;
    private _hovered = false;
    private _mutationObserver!: MutationObserver;
    private _overlay!: HTMLElement | null;
    private _player!: AnimationPlayer;
    private _scrollStrategy: ScrollStrategy = inject(
        ScrollStrategyOptions
    ).block();
    private _fuseScrollbarDirectives!: QueryList<FuseScrollbarDirective>;
    private _fuseScrollbarDirectivesSubscription!: Subscription;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    private dialog = inject(MatDialog);
    private _userService = inject(UserService);
    private _navigationService = inject(NavigationService);
    private _animationBuilder = inject(AnimationBuilder);
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _document = inject(DOCUMENT);
    private _elementRef = inject(ElementRef);
    private _renderer2 = inject(Renderer2);
    private _router = inject(Router);
    private _fuseNavigationService = inject(FuseNavigationService);
    private _fuseUtilsService = inject(FuseUtilsService);
    public snackBar = inject(MatSnackBar);
    private _scrollStrategyOptions = inject(ScrollStrategyOptions);
    private _translocoService = inject(TranslocoService);

    company!: Company;
    supplier: any;
    companyId: string = '';

    role: string = '';
    selectedBranch!: number;
    permissions: Record<string, boolean> = {};
    permissionsLoading = true;
    readonly navSkeletonItems: { width: string; hasArrow: boolean }[] = [
        { width: '70%', hasArrow: false },   // Dashboard
        { width: '50%', hasArrow: false },   // Tracker
        { width: '40%', hasArrow: false },   // Data Sources
        { width: '70%', hasArrow: false },   // Procedures
        { width: '62%', hasArrow: false },   // Activities
        { width: '55%', hasArrow: true },    // Reports
        { width: '50%', hasArrow: true },    // SAI Chat
    ];

    constructor() {
        this._handleAsideOverlayClick = (): void => this.closeAside();
        this._handleOverlayClick = (): void => this.close();
    }

    // ---------- Actions ----------
    collapseClicked(_item: NavItem) {}
    clickOnBasicItem(_item: NavItem) {}
    checkIfChainExists(_item: NavItem) {}

    openSnackBar(message: string, panelClass: string) {
        this.snackBar.open(message, 'Close', {
            duration: 2000,
            horizontalPosition: 'end',
            verticalPosition: 'top',
            panelClass: [panelClass],
        });
    }

    // ---------- Host bindings ----------
    @HostBinding('class') get classList(): any {
        return {
            'fuse-vertical-navigation-animations-enabled':
                this._animationsEnabled,
            [`fuse-vertical-navigation-appearance-${this.appearance}`]: true,
            'fuse-vertical-navigation-hover': this._hovered,
            'fuse-vertical-navigation-inner': this.inner,
            'fuse-vertical-navigation-mode-over': this.mode === 'over',
            'fuse-vertical-navigation-mode-side': this.mode === 'side',
            'fuse-vertical-navigation-opened': this.opened,
            'fuse-vertical-navigation-position-left': this.position === 'left',
            'fuse-vertical-navigation-position-right':
                this.position === 'right',
        };
    }

    @HostBinding('style') get styleList(): any {
        return { visibility: this.opened ? 'visible' : 'hidden' };
    }

    // ---------- Scrollbars ----------
    @ViewChildren(FuseScrollbarDirective)
    set fuseScrollbarDirectives(
        fuseScrollbarDirectives: QueryList<FuseScrollbarDirective>
    ) {
        this._fuseScrollbarDirectives = fuseScrollbarDirectives;

        if (fuseScrollbarDirectives.length === 0) return;

        if (this._fuseScrollbarDirectivesSubscription) {
            this._fuseScrollbarDirectivesSubscription.unsubscribe();
        }

        this._fuseScrollbarDirectivesSubscription = merge(
            this.onCollapsableItemCollapsed,
            this.onCollapsableItemExpanded
        )
            .pipe(takeUntil(this._unsubscribeAll), delay(250))
            .subscribe(() => {
                fuseScrollbarDirectives.forEach(d => d.update());
            });
    }

    // ---------- Mouse ----------
    @HostListener('mouseenter') private _onMouseenter(): void {
        this._enableAnimations();
        this._hovered = true;
    }
    @HostListener('mouseleave') private _onMouseleave(): void {
        this._enableAnimations();
        this._hovered = false;
    }

    // ---------- Lifecycle ----------
    ngOnChanges(changes: SimpleChanges): void {
        if ('appearance' in changes)
            this.appearanceChanged.next(changes.appearance.currentValue);
        if ('inner' in changes)
            this.inner = coerceBooleanProperty(changes.inner.currentValue);

        if ('mode' in changes) {
            const currentMode = changes.mode.currentValue;
            const previousMode = changes.mode.previousValue;

            this._disableAnimations();

            if (previousMode === 'over' && currentMode === 'side')
                this._hideOverlay();
            if (previousMode === 'side' && currentMode === 'over') {
                this.closeAside();
                if (this.opened) this._showOverlay();
            }

            this.modeChanged.next(currentMode);
            setTimeout(() => this._enableAnimations(), 500);
        }

        if ('navigation' in changes) {
            this.rebuildNav();
            this._changeDetectorRef.markForCheck();
        }

        if ('opened' in changes) {
            this.opened = coerceBooleanProperty(changes.opened.currentValue);
            this._toggleOpened(this.opened);
        }

        if ('position' in changes)
            this.positionChanged.next(changes.position.currentValue);
        if ('transparentOverlay' in changes)
            this.transparentOverlay = coerceBooleanProperty(
                changes.transparentOverlay.currentValue
            );
    }

    ngOnInit(): void {
        this.role = localStorage.getItem('userRole') || '';
        this.permissionsLoading = true;

        this._userService
            .getUserPermissions()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (res: any) => {
                    this.permissions = res ?? {};
                    this.permissionsLoading = false;
                    this.rebuildNav();
                    this._changeDetectorRef.markForCheck();
                },
                error: () => {
                    this.permissions = {};
                    this.permissionsLoading = false;
                    this.rebuildNav();
                    this._changeDetectorRef.markForCheck();
                },
            });

        if (!this.name) this.name = this._fuseUtilsService.randomId();
        this._fuseNavigationService.registerComponent(this.name, this);

        this._router.events
            .pipe(
                filter(e => e instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                if (this.mode === 'over' && this.opened) this.close();
                if (this.mode === 'side' && this.activeAsideItemId)
                    this.closeAside();
            });

        // أول بناء للشجرة
        this.rebuildNav();

        // Subscribe to language changes to update navigation
        this._translocoService.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.rebuildNav();
                this._changeDetectorRef.markForCheck();
            });
    }

    ngAfterViewInit(): void {
        this._mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation: any) => {
                const target = mutation.target as HTMLElement;
                if (mutation.attributeName === 'class') {
                    if (target.classList.contains('cdk-global-scrollblock')) {
                        const top = parseInt(target.style.top, 10);
                        this._renderer2.setStyle(
                            this._elementRef.nativeElement,
                            'margin-top',
                            `${Math.abs(top)}px`
                        );
                    } else {
                        this._renderer2.setStyle(
                            this._elementRef.nativeElement,
                            'margin-top',
                            null
                        );
                    }
                }
            });
        });
        this._mutationObserver.observe(this._document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });

        setTimeout(() => {
            if (!this._navigationContentEl) return;

            if (
                !this._navigationContentEl.nativeElement.classList.contains(
                    'ps'
                )
            ) {
                const active =
                    this._navigationContentEl.nativeElement.querySelector(
                        '.fuse-vertical-navigation-item-active'
                    );
                if (active) active.scrollIntoView();
            } else {
                this._fuseScrollbarDirectives?.forEach((d) => {
                    if (!d.isEnabled()) return;
                    d.scrollToElement(
                        '.fuse-vertical-navigation-item-active',
                        -120,
                        true
                    );
                });
            }
        });
    }

    ngOnDestroy(): void {
        this._mutationObserver?.disconnect();
        this.close();
        this.closeAside();
        this._fuseNavigationService.deregisterComponent(this.name);
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // ---------- Filtering Logic ----------
    private isVisible(item: NavItem, effectiveRoles: string[]): boolean {
        const roleOk =
            effectiveRoles.length === 0
                ? true
                : effectiveRoles.includes(this.role);
        const permOk =
            !item.permissionKey || this.permissions?.[item.permissionKey] === true;
        const hiddenVal = item.hidden as any;
        const hiddenOk =
            typeof hiddenVal === 'function' ? !hiddenVal(item) : !hiddenVal;
        return roleOk && permOk && hiddenOk;
    }

    private filterTree(
        items: NavItem[],
        inheritedRoles: string[] = []
    ): NavItem[] {
        return (items ?? [])
            .map<NavItem | null>((i) => {
                const selfRoles = this.getItemRoles(i);
                const effectiveRoles = selfRoles.length
                    ? selfRoles
                    : inheritedRoles;

                const filteredChildren = this.filterTree(
                    i.children ?? [],
                    effectiveRoles
                );

                // If collapsable ends up with exactly one child, convert to basic and take child's title/link.
                if (i.type === 'collapsable' && filteredChildren.length === 1) {
                    const only = filteredChildren[0];

                    // Ensure the single child itself is visible under the same role/perm rules
                    if (!this.isVisible(only, effectiveRoles)) return null;

                    // Flatten: keep parent's id/icon unless child overrides, adopt child's title/link
                    return {
                        ...i,
                        ...only,
                        id: i.id ?? only.id, // keep stable id
                        title: only.title, // take child's title
                        type: 'basic', // convert to basic
                        link: (only as any).link, // take child's link
                        icon: i.icon ?? (only as any).icon,
                        children: [], // no children for basic
                    } as NavItem;
                }

                const showSelf = this.isVisible(i, effectiveRoles);
                const showChildren = filteredChildren.length > 0;

                if (i.type === 'collapsable') {
                    // Parent permission must pass as well; do not render a
                    // collapsable group only because its children exist.
                    return showSelf && showChildren
                        ? { ...i, children: filteredChildren }
                        : null;
                }

                return showSelf ? { ...i, children: filteredChildren } : null;
            })
            .filter((x): x is NavItem => !!x);
    }

    private rebuildNav(): void {
        // Translate navigation items before filtering
        const translatedNavigation = this.translateNavigationItems(
            this.navigation ?? []
        );
        this.navToRender = this.filterTree(translatedNavigation);
        this._changeDetectorRef.markForCheck();
    }

    private translateNavigationItems(items: NavItem[]): NavItem[] {
        return items.map((item) => {
            const translatedItem = { ...item };

            // Translate the title if it exists
            if (item.title) {
                translatedItem.title = this._translocoService.translate(
                    item.title
                );
            }

            // Translate subtitle if it exists
            if (item.subtitle) {
                translatedItem.subtitle = this._translocoService.translate(
                    item.subtitle
                );
            }

            // Translate tooltip if it exists
            if (item.tooltip) {
                translatedItem.tooltip = this._translocoService.translate(
                    item.tooltip
                );
            }

            // Translate badge title if it exists
            if (item.badge?.title) {
                translatedItem.badge = {
                    ...item.badge,
                    title: this._translocoService.translate(item.badge.title),
                };
            }

            // Recursively translate children
            if (item.children && item.children.length > 0) {
                translatedItem.children = this.translateNavigationItems(
                    item.children
                );
            }

            return translatedItem;
        });
    }

    // ---------- Public API ----------
    refresh(): void {
        this._changeDetectorRef.markForCheck();
        this.onRefreshed.next(true);
    }

    open(): void {
        if (this.opened) return;
        this._toggleOpened(true);
    }
    close(): void {
        if (!this.opened) return;
        this.closeAside();
        this._toggleOpened(false);
    }
    toggle(): void {
        this.opened ? this.close() : this.open();
    }

    openAside(item: FuseNavigationItem): void {
        if (item.disabled || !item.id) return;
        this.activeAsideItemId = item.id;
        this._showAsideOverlay();
        this._changeDetectorRef.markForCheck();
    }
    closeAside(): void {
        this.activeAsideItemId = null;
        this._hideAsideOverlay();
        this._changeDetectorRef.markForCheck();
    }
    toggleAside(item: FuseNavigationItem): void {
        this.activeAsideItemId === item.id
            ? this.closeAside()
            : this.openAside(item);
    }

    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // ---------- Animations / Overlays ----------
    private _enableAnimations(): void {
        if (this._animationsEnabled) return;
        this._animationsEnabled = true;
    }
    private _disableAnimations(): void {
        if (!this._animationsEnabled) return;
        this._animationsEnabled = false;
    }

    private _showOverlay(): void {
        if (this._overlay) return;

        this._overlay = this._renderer2.createElement('div');
        this._overlay.classList.add('fuse-vertical-navigation-overlay');
        if (this.transparentOverlay)
            this._overlay.classList.add(
                'fuse-vertical-navigation-overlay-transparent'
            );

        this._renderer2.appendChild(
            this._elementRef.nativeElement.parentElement,
            this._overlay
        );
        this._scrollStrategy.enable();

        this._player = this._animationBuilder
            .build([
                animate(
                    '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                    style({ opacity: 1 })
                ),
            ])
            .create(this._overlay);
        this._player.play();

        this._overlay.addEventListener('click', this._handleOverlayClick);
    }

    private _hideOverlay(): void {
        if (!this._overlay) return;

        this._player = this._animationBuilder
            .build([
                animate(
                    '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                    style({ opacity: 0 })
                ),
            ])
            .create(this._overlay);
        this._player.play();

        this._player.onDone(() => {
            if (this._overlay) {
                this._overlay.removeEventListener(
                    'click',
                    this._handleOverlayClick
                );
                this._overlay.parentNode?.removeChild(this._overlay);
                this._overlay = null;
            }
            this._scrollStrategy.disable();
        });
    }

    private _showAsideOverlay(): void {
        if (this._asideOverlay) return;

        this._asideOverlay = this._renderer2.createElement('div');
        this._asideOverlay.classList.add(
            'fuse-vertical-navigation-aside-overlay'
        );
        this._renderer2.appendChild(
            this._elementRef.nativeElement.parentElement,
            this._asideOverlay
        );

        this._player = this._animationBuilder
            .build([
                animate(
                    '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                    style({ opacity: 1 })
                ),
            ])
            .create(this._asideOverlay);
        this._player.play();

        this._asideOverlay.addEventListener(
            'click',
            this._handleAsideOverlayClick
        );
    }

    private _hideAsideOverlay(): void {
        if (!this._asideOverlay) return;

        this._player = this._animationBuilder
            .build([
                animate(
                    '300ms cubic-bezier(0.25, 0.8, 0.25, 1)',
                    style({ opacity: 0 })
                ),
            ])
            .create(this._asideOverlay);
        this._player.play();

        this._player.onDone(() => {
            if (this._asideOverlay) {
                this._asideOverlay.removeEventListener(
                    'click',
                    this._handleAsideOverlayClick
                );
                this._asideOverlay.parentNode?.removeChild(this._asideOverlay);
                this._asideOverlay = null;
            }
        });
    }

    private _toggleOpened(open: boolean): void {
        this.opened = open;
        this._enableAnimations();

        if (this.mode === 'over') {
            this.opened ? this._showOverlay() : this._hideOverlay();
        }
        // SAI PATCH: mark this OnPush component dirty after an imperative
        // open/close/toggle. The drawer's visible state is host bindings
        // (`fuse-vertical-navigation-opened` + `visibility`), so it only paints
        // when this view is checked, while `_showOverlay()` appends the backdrop
        // straight to the DOM. Without this, a page whose change-detection pass
        // does not reach the layout view leaves the user with a dimmed screen
        // and no drawer (reported on Activities at phone widths).
        this._changeDetectorRef.markForCheck();
        this.openedChanged.next(open);
    }

    private getItemRoles(item: NavItem): string[] {
        return [item.role, item.secondRole, item.thirdRole].filter(
            Boolean
        ) as string[];
    }

    isItemVisible(_item: any): boolean {
        // SAI PATCH: production nav visibility used to hard-hide shipping features
        // (e.g. tasks). Team Chat / Tasks / etc. are gated by role + permissionKey via
        // filterTree/isVisible instead — keep this hook for future prod-only hides.
        return true;
    }
}
